<template>
  <v-dialog
    :model-value="true"
    :retain-focus="false"
    :width="width"
    :max-width="maxWidth"
    @update:model-value="handleDialogUpdate"
  >
    <section
      class="detail-dialog"
      role="dialog"
      aria-modal="true"
      :aria-label="title"
      :style="{ maxHeight }"
    >
      <header class="detail-dialog__header">
        <div class="detail-dialog__title">
          <v-icon
            :icon="icon"
            color="#3ACAC9"
            size="20"
          />
          <h2>{{ title }}</h2>
        </div>
        <div class="detail-dialog__header-actions">
          <v-btn
            v-if="showDelete"
            icon="mdi-trash-can-outline"
            variant="text"
            size="small"
            color="rgba(0, 0, 0, 0.54)"
            :disabled="isSaving"
            :aria-label="`${title}を削除`"
            @click="$emit('delete')"
          />
          <slot name="header-extra" />
        </div>
      </header>

      <div
        class="detail-dialog__body"
        :style="{ padding: bodyPadding }"
      >
        <slot />
      </div>

      <footer class="detail-dialog__actions">
        <v-btn
          v-if="showCancel"
          variant="outlined"
          :min-width="cancelMinWidth"
          :disabled="isSaving"
          @click="$emit('cancel')"
        >
          {{ cancelLabel }}
        </v-btn>
        <v-btn
          v-if="showSave"
          color="#3ACAC9"
          variant="flat"
          min-width="88"
          :disabled="saveDisabled || isSaving"
          :loading="isSaving"
          @click="$emit('save')"
        >
          {{ saveLabel }}
        </v-btn>
      </footer>
    </section>
  </v-dialog>
</template>

<script>
export default {
  name: "DetailDialog",
  props: {
    title: {
      type: String,
      required: true
    },
    icon: {
      type: String,
      default: "mdi-file-document-outline"
    },
    width: {
      type: [String, Number],
      default: 860
    },
    maxWidth: {
      type: String,
      default: "calc(100vw - 64px)"
    },
    maxHeight: {
      type: String,
      default: "calc(100vh - 64px)"
    },
    bodyPadding: {
      type: String,
      default: "0"
    },
    showCancel: {
      type: Boolean,
      default: true
    },
    showSave: {
      type: Boolean,
      default: true
    },
    saveDisabled: {
      type: Boolean,
      default: false
    },
    // 削除ボタン（ゴミ箱アイコン）をヘッダー右上に表示するかどうか。
    // 新規登録可能なイベントの既存レコードを開いたときのみ true にする想定。
    showDelete: {
      type: Boolean,
      default: false
    },
    cancelLabel: {
      type: String,
      default: "キャンセル"
    },
    saveLabel: {
      type: String,
      default: "保存"
    },
    cancelMinWidth: {
      type: [String, Number],
      default: 112
    },
    // 保存処理中フラグ。true の間は保存ボタンを無効化＆ローディング表示し、
    // 連打による重複登録を防ぐ。親が保存完了まで制御する。
    isSaving: {
      type: Boolean,
      default: false
    }
  },
  emits: ["cancel", "save", "delete"],
  methods: {
    // 保存処理中（isSaving=true）は外側クリック・Escによる閉じる操作を無視し、
    // 保存が裏で進行中にダイアログが閉じて誤認されるのを防ぐ。
    handleDialogUpdate(value) {
      if (this.isSaving) return;
      if (!value) this.$emit("cancel");
    }
  }
};
</script>

<style lang="scss" scoped>
.detail-dialog {
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.28);
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  z-index: 1;
}

.detail-dialog__header {
  align-items: center;
  border-bottom: 1px solid #eeeeee;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  min-height: 56px;
  padding: 0 24px;
}

.detail-dialog__title {
  align-items: center;
  display: flex;
  gap: 8px;
}

.detail-dialog__header-actions {
  align-items: center;
  display: flex;
  gap: 8px;
}

.detail-dialog__title h2 {
  color: rgba(0, 0, 0, 0.78);
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  margin: 0;
}

.detail-dialog__body {
  overflow-x: visible;
  overflow-y: auto;
}

.detail-dialog__actions {
  align-items: center;
  background-color: #fafafa;
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  padding: 18px 24px;
}

/* 共通フォーム部品（スロット内で使用される要素を横断的にスタイリング） */
:slotted(.field-block) {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

:slotted(.field-label) {
  color: rgba(0, 0, 0, 0.58);
  font-size: 12px;
  line-height: 1.2;
}

:slotted(.field-value) {
  color: rgba(0, 0, 0, 0.72);
  font-size: 13px;
  line-height: 1.5;
}

:slotted(.text-input) {
  background-color: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 3px;
  color: rgba(0, 0, 0, 0.72);
  font-size: 12px;
  height: 32px;
  line-height: 1.5;
  padding: 6px 8px;
  width: 100%;
}

:slotted(.editable-note) {
  background-color: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 3px;
  color: rgba(0, 0, 0, 0.72);
  font-size: 12px;
  line-height: 1.5;
  min-height: 72px;
  padding: 8px 10px;
  resize: vertical;
  width: 100%;
}

:slotted(.readonly-note) {
  background-color: #fafafa;
  border: 1px solid #d9d9d9;
  border-radius: 3px;
  color: rgba(0, 0, 0, 0.72);
  font-size: 12px;
  line-height: 1.5;
  min-height: 72px;
  padding: 8px 10px;
  resize: none;
  width: 100%;
}

:slotted(.choice-row) {
  align-items: center;
  align-self: flex-start;
  color: rgba(0, 0, 0, 0.76);
  display: flex;
  font-size: 12px;
  gap: 4px;
  line-height: 1.45;
  white-space: nowrap;
}

:slotted(.choice-row input) {
  height: 13px;
  margin: 0;
  width: 13px;
}
</style>
